/**
 * 判断局部边条件是否会改变整边求解范围。
 *
 * 设计说明：
 * 1. `none` 与 `vertex` 都不会改变默认“整条边”的起止范围；
 * 2. 真正需要进入 partial-edge 分支的，是会引入偏移截断的条件；
 * 3. 这样可以把“UI 默认占位参数”与“显式启用部分边圆角”从底层语义上彻底分开。
 *
 * @param condition 局部边条件。
 * @returns 是否需要局部截断求解。
 */
export function isRangeCuttingPartialCondition(
    condition: BlendPartialCondition | undefined,
): boolean {
    return condition === 'offset'
        || condition === 'percentage-offset'
        || condition === 'reference-offset';
}

/**
 * 判断单条局部边参数是否真的启用了部分边求解。
 *
 * 设计说明：
 * 1. 仅仅存在 `PartialEdgeParam` 记录，并不代表用户已经启用了部分边；
 * 2. 只有开始端或结束端出现真实截断条件时，才允许进入 partial-edge solver；
 * 3. 该判定会同时服务于 preview、正式提交和 capability 校验，避免三条链各自解释。
 *
 * @param partialEdge 局部边参数。
 * @returns 是否真的启用了部分边求解。
 */
export function isActivePartialEdgeParam(
    partialEdge: PartialEdgeParam | null | undefined,
): boolean {
    if (!partialEdge) {
        return false;
    }
    return isRangeCuttingPartialCondition(partialEdge.startCondition)
        || isRangeCuttingPartialCondition(partialEdge.endCondition);
}

/**
 * 过滤出真正激活的局部边参数。
 *
 * 设计说明：
 * 1. web-client 会为每条已选边预先创建默认占位参数，方便后续直接编辑；
 * 2. 这些默认占位不能污染 worker 的求解分支选择；
 * 3. 因此 worker 在进入任何 partial-edge 链路前，都必须先通过这里统一过滤。
 *
 * @param partialEdges 局部边参数集合。
 * @returns 真正激活的局部边参数集合。
 */
export function filterActivePartialEdgeParams(
    partialEdges: PartialEdgeParam[] | undefined,
    enabled: boolean = true,
): PartialEdgeParam[] {
    if (!enabled) {
        return [];
    }
    return (partialEdges ?? []).filter((partialEdge) => isActivePartialEdgeParam(partialEdge));
}
